import { useEffect, useState } from "react";
import { Loader2, BadgeCheck, AlertTriangle, Clock } from "lucide-react";
import { lagDays, recLag } from "@/lib/spot";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const PAY_MODES = ["Razorpay", "Cash", "UPI", "Cheque", "Card", "NEFT/RTGS", "Demand Draft"];
const REF_LABEL = { UPI: "UPI Transaction ID", Cheque: "Cheque No.", Card: "Card Txn / Approval Code", "NEFT/RTGS": "UTR No.", "Demand Draft": "DD No.", Razorpay: "Razorpay Payment ID" };

export const PaymentDialog = ({ open, onOpenChange, defaultAmount, initial, onConfirm, receiptNo, warnFor, editHref, title = "Payment received", confirmLabel = "Confirm Admission", requireNote = false, partial = null }) => {
  const blank = () => ({ mode: "Razorpay", reference: "", amount: defaultAmount || "", date: new Date().toLocaleDateString("en-CA") });
  const [p, setP] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setP({ ...(initial ? { ...initial } : blank()), ...(partial ? { only_ff: partial.onlyFormFee, balance_date: partial.balanceDate || "" } : {}) });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const needRef = p.mode !== "Cash";
  const saved = !!(initial?.recorded_at && initial.date === p.date);
  const issues = (warnFor ? warnFor(p.date) : []).filter((x) => !(saved && /^(Admission fee was recorded|Form fee is being recorded)/.test(x)));
  const needNote = requireNote && issues.length > 0;
  const late = warnFor && !partial ? (saved ? recLag(initial) : lagDays(p.date)) : 0;
  const onlyFF = !!(partial && p.only_ff);
  const payValid = Number(p.amount) > 0 && p.date && (!needRef || p.reference.trim()) && (late < 1 || (p.late_reason || "").trim().length >= 3);
  const valid = partial
    ? !!p.balance_date && p.balance_date >= (onlyFF ? "" : p.date || "") && (onlyFF || (payValid && Number(p.amount) < partial.due))
    : payValid && (!needNote || (p.spot_note || "").trim());
  const submit = async () => {
    setBusy(true);
    const { mode, reference, amount, date, spot_note, late_reason } = p;
    const pay = { mode, date, amount: Number(amount), reference: reference.trim(), spot_note: needNote ? (spot_note || "").trim() : "", late_reason: late >= 1 ? (late_reason || "").trim() : "" };
    await onConfirm(partial ? { payment: onlyFF ? null : pay, only_form_fee: onlyFF, balance_date: p.balance_date } : pay);
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{title} {receiptNo ? `· ${receiptNo}` : ""}</DialogTitle>
          <DialogDescription>These details are printed on the receipt as proof of payment.</DialogDescription>
        </DialogHeader>
        {partial && (
          <div className="flex rounded-md border border-plum-900/15 p-0.5 text-xs" data-testid="partial-kind">
            <button type="button" onClick={() => setP({ ...p, only_ff: false })} data-testid="partial-kind-part"
              className={`flex-1 rounded px-2 py-1.5 font-medium ${!onlyFF ? "bg-plum-900 text-white" : "text-slate-600"}`}>Part of admission amount</button>
            <button type="button" disabled={!partial.hasFormFee} onClick={() => setP({ ...p, only_ff: true })} data-testid="partial-kind-form-fee"
              title={partial.hasFormFee ? "" : "Record the form fee first"}
              className={`flex-1 rounded px-2 py-1.5 font-medium disabled:opacity-40 ${onlyFF ? "bg-plum-900 text-white" : "text-slate-600"}`}>Only the form fee{partial.hasFormFee ? "" : " (record it first)"}</button>
          </div>
        )}
        {!onlyFF && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Payment Mode</Label>
            <Select value={p.mode} onValueChange={(v) => setP({ ...p, mode: v })}>
              <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m} value={m} data-testid={`payment-mode-${m.replace(/\W+/g, "-").toLowerCase()}`}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Amount Paid (₹)</Label>
            <Input type="number" value={p.amount} onChange={(e) => setP({ ...p, amount: e.target.value })} data-testid="payment-amount-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">{needRef ? `${REF_LABEL[p.mode]} *` : "Reference (optional)"}</Label>
            <Input value={p.reference} onChange={(e) => setP({ ...p, reference: e.target.value })} data-testid="payment-reference-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-600">Payment Date</Label>
            <Input type="date" value={p.date} onChange={(e) => setP({ ...p, date: e.target.value })} data-testid="payment-date-input" />
          </div>
        </div>
        )}
        {partial && (
          <div className="grid gap-4 rounded-md border border-orange-200 bg-orange-50 p-3 sm:grid-cols-2">
            <div className="text-xs text-orange-900">
              <div className="uppercase tracking-wider">Balance due</div>
              <div className="font-num mt-1 text-lg font-bold" data-testid="partial-balance">₹ {Math.max(0, partial.due - (onlyFF ? 0 : Number(p.amount) || 0)).toLocaleString("en-IN")}</div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-orange-900">Collect balance by *</Label>
              <Input type="date" min={onlyFF ? undefined : p.date} value={p.balance_date || ""} onChange={(e) => setP({ ...p, balance_date: e.target.value })} className="bg-white" data-testid="partial-balance-date-input" />
            </div>
            {!onlyFF && Number(p.amount) >= partial.due && partial.due > 0 && (
              <p className="text-xs font-medium text-orange-900 sm:col-span-2" data-testid="partial-full-warning">This covers the full balance — use "Mark as Admitted" instead.</p>
            )}
          </div>
        )}
        {late >= 1 && (
          <div className={`rounded-md border p-3 text-xs ${late === 1 ? "border-sky-300 bg-sky-50 text-sky-900" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="payment-late-entry">
            <div className="mb-1 flex items-center gap-1.5 font-bold"><Clock className="h-4 w-4" />
              {late === 1 ? "Recorded next day - Spot still applies" : `Recorded ${late} days after payment`}</div>
            <p>{late === 1 ? "Late-evening payments can be entered until the next day. This entry will be marked 'Recorded next day' for the accountant to check." : "Only next-day entry keeps Spot; this will be treated as a Spot exception."}</p>
            <Label className="mt-2 block text-xs font-bold">Why is it recorded late? *</Label>
            <Textarea rows={2} maxLength={300} value={p.late_reason || ""} onChange={(e) => setP({ ...p, late_reason: e.target.value })}
              placeholder="e.g. Parent paid on Razorpay at 9:40 PM after office hours" className="mt-1 bg-white text-xs" data-testid="payment-late-reason-input" />
          </div>
        )}
        {issues.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="payment-spot-warning">
            <div className="mb-1 flex items-center gap-1.5 font-bold"><AlertTriangle className="h-4 w-4" />Spot benefits may not apply</div>
            <ul className="list-disc space-y-0.5 pl-5">{issues.map((x) => <li key={x}>{x}</li>)}</ul>
            <p className="mt-1.5">Spot needs the Rs.1000 form fee on the enquiry date and the admission fee within 5 days of the enquiry. You can still save; the receipt will be flagged.
              {editHref && <a href={editHref} className="ml-1 font-semibold underline" data-testid="payment-edit-receipt-link">Edit receipt</a>}</p>
            {needNote && (
              <div className="mt-2.5 space-y-1">
                <Label className="text-xs font-bold text-amber-900">Reason for exception *</Label>
                <Textarea rows={2} maxLength={300} value={p.spot_note || ""} onChange={(e) => setP({ ...p, spot_note: e.target.value })}
                  placeholder="e.g. Principal approved Spot discount; parent paid form fee next morning" className="bg-white text-xs" data-testid="payment-spot-note-input" />
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="payment-cancel-btn">Cancel</Button>
          <Button disabled={!valid || busy} onClick={submit} className="bg-plum-700 hover:bg-plum-800" data-testid="payment-confirm-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <BadgeCheck className="mr-2 h-4 w-4" />}{confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
